import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import '../css/Pos.css';

/**
 * A dropdown that can actually be styled.
 *
 * WHY NOT A NATIVE `<select>`. The options inside one are drawn by the
 * operating system, not by this app: `padding` on an `<option>` is ignored or
 * half-honoured depending on the platform, the list cannot take the club's
 * colours, and there is no way to mark the current choice beyond whatever
 * highlight the OS happens to use. The first version of the sort control used
 * one and the list came out cramped and unthemed on Windows. This renders the
 * list itself, so the padding and the selected row are ours.
 *
 * It is a listbox in the ARIA sense — a button that owns a list — so screen
 * readers and the keyboard behave the way they do on a real select: arrows
 * move, Enter and Space choose, Escape closes and returns focus, Home and End
 * jump to the ends, Tab leaves.
 *
 * THE HIGHLIGHT IS TWO DIFFERENT THINGS, deliberately. `selected` is the
 * option in force and stays marked wherever the pointer goes; `active` is the
 * row the keyboard or mouse is currently on. Collapsing them into one style
 * loses track of what you had chosen the moment you move the mouse.
 *
 * @param {object} props
 * @param {Array<{key: string, label: string}>} props.options - never empty
 * @param {string} props.value - the key in force
 * @param {(key: string) => void} props.onChange
 * @param {string} [props.label] - a short word shown before the value, and the
 *   accessible name of the control
 * @param {string} [props.className] - added to the wrapper
 * @returns {JSX.Element}
 */
function PosSelect({ options, value, onChange, label = '', className = '' }) {
  const [open, setOpen] = useState(false);
  // Which row the keyboard is on. Separate from `value`: moving through the
  // list must not change the setting until it is chosen.
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const id = useId();
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;

  const selectedIndex = Math.max(0, options.findIndex((option) => option.key === value));
  const selected = options[selectedIndex] || options[0];

  /**
   * Opens the list with the keyboard sitting on the option already in force,
   * so the first arrow press moves from where you are rather than from the top.
   * @returns {void}
   */
  const openList = () => {
    setActiveIndex(selectedIndex);
    setOpen(true);
  };

  /**
   * Closes the list and puts focus back on the button.
   *
   * Focus has to be returned by hand: it was moved into the list when that
   * opened, and a list that is about to be unmounted cannot hold it — without
   * this, closing with Escape drops focus to the document body and the next
   * Tab starts from the top of the page.
   *
   * @returns {void}
   */
  const closeList = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  /**
   * Chooses one option and closes.
   * @param {string} key
   * @returns {void}
   */
  const choose = (key) => {
    onChange(key);
    closeList();
  };

  // Focus moves into the list as it opens, which is what makes the arrow keys
  // reach it at all.
  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  // A click anywhere else closes it. `pointerdown` rather than `click` so the
  // list is gone before the thing underneath reacts — on `click`, tapping a
  // product tile behind an open list would both close the list and open that
  // product.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  /**
   * Keys on the closed button. Down, Enter and Space all open — the three a
   * person reaches for, and the two a native select answers to.
   * @param {React.KeyboardEvent} event
   * @returns {void}
   */
  const onButtonKeyDown = (event) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      openList();
    }
  };

  /**
   * Keys while the list is open.
   * @param {React.KeyboardEvent} event
   * @returns {void}
   */
  const onListKeyDown = (event) => {
    const last = options.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => Math.min(last, i + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(last);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(options[activeIndex]?.key ?? value);
        break;
      case 'Escape':
        event.preventDefault();
        closeList();
        break;
      case 'Tab':
        // Not prevented: Tab should still move on. The list just goes with it.
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div className={`pos-select${open ? ' open' : ''}${className ? ` ${className}` : ''}`} ref={wrapRef}>
      {label && <span className="pos-select-label" id={labelId}>{label}</span>}

      <button
        type="button"
        ref={buttonRef}
        className="pos-select-value"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${label ? `${labelId} ` : ''}${valueId}`}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onButtonKeyDown}
      >
        <span id={valueId} className="pos-select-text">{selected?.label}</span>
        <ChevronDown size={14} aria-hidden="true" className="pos-select-caret" />
      </button>

      {open && (
        <ul
          className="pos-select-menu"
          role="listbox"
          ref={listRef}
          tabIndex={-1}
          aria-labelledby={label ? labelId : undefined}
          aria-activedescendant={`${id}-opt-${activeIndex}`}
          onKeyDown={onListKeyDown}
        >
          {options.map((option, index) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <li
              key={option.key}
              id={`${id}-opt-${index}`}
              role="option"
              aria-selected={option.key === selected?.key}
              className={[
                'pos-select-option',
                option.key === selected?.key ? 'selected' : '',
                index === activeIndex ? 'active' : '',
              ].filter(Boolean).join(' ')}
              onMouseEnter={() => setActiveIndex(index)}
              // The list holds focus; letting the mouse take it away would
              // close the list through the outside-click handler before the
              // click below ever lands.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(option.key)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PosSelect;
